import type { VideoCallPanelSize } from "~/types/videoCalls";
import { VIDEO_CALL_PANEL_SIZE_ORDER } from "~/types/videoCalls";

export interface VideoCallPanelOffset {
  x: number;
  y: number;
}

export interface VideoCallPanelBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface VideoCallPanelViewport {
  width: number;
  height: number;
}

export interface ClampVideoCallPanelOffsetInput {
  /** The offset the drag is asking for. */
  offset: VideoCallPanelOffset;
  /** The offset already applied to `box`, used to recover the anchored position. */
  appliedOffset: VideoCallPanelOffset;
  /** The panel's measured box, transform included. */
  box: VideoCallPanelBox;
  viewport: VideoCallPanelViewport;
}

/**
 * Keeps the panel fully inside the viewport. Dropping it outside would take the
 * hang-up control off screen and leave the user stuck in the call.
 *
 * When the panel is larger than the viewport the two bounds cannot both hold;
 * the top-left corner wins, because that is where the header and its controls
 * live.
 */
export const clampVideoCallPanelOffset = ({
  offset,
  appliedOffset,
  box,
  viewport,
}: ClampVideoCallPanelOffsetInput): VideoCallPanelOffset => {
  const anchoredLeft = box.left - appliedOffset.x;
  const anchoredTop = box.top - appliedOffset.y;

  const minX = -anchoredLeft;
  const maxX = viewport.width - anchoredLeft - box.width;
  const minY = -anchoredTop;
  const maxY = viewport.height - anchoredTop - box.height;

  return {
    x: maxX < minX ? minX : Math.min(Math.max(offset.x, minX), maxX),
    y: maxY < minY ? minY : Math.min(Math.max(offset.y, minY), maxY),
  };
};

/**
 * Returns the neighbouring size, or null at the ends of the scale so callers
 * can disable the control instead of silently doing nothing.
 */
export const getAdjacentVideoCallPanelSize = (
  size: VideoCallPanelSize,
  offset: 1 | -1,
): VideoCallPanelSize | null => {
  const index = VIDEO_CALL_PANEL_SIZE_ORDER.indexOf(size);
  if (index < 0) return null;
  return VIDEO_CALL_PANEL_SIZE_ORDER[index + offset] ?? null;
};
